<template>
    <div>
        <v-breadcrumbs class="pa-0 mb-3 flex-wrap" :items="breadcrumbItems" divider=">" color="primary">
            <v-spacer />
            <v-tooltip v-if="!isB2C && userGuideLinkConfluence" :text="$t('user_guide_links.link')" location="bottom">
                <template v-slot:activator="{props}">
                    <v-btn
                        v-bind="props"
                        variant="text"
                        target="_blank"
                        icon="mdi-head-question"
                        :href="userGuideLinkConfluence"
                    />
                </template>
            </v-tooltip>
            <v-tooltip v-if="!isB2C && userGuideLinkVideo" :text="$t('user_guide_links.video')" location="bottom">
                <template v-slot:activator="{props}">
                    <v-btn v-bind="props" :href="userGuideLinkVideo" variant="text" target="_blank" icon="mdi-video" />
                </template>
            </v-tooltip>
        </v-breadcrumbs>
        <v-card class="mt-4 pa-4" variant="outlined">
            <h1 class="text-h4 mb-2">
                {{ $t('reservation_statements') }}
                <span v-if="supplierName" class="ml-2">- {{ supplierName }}</span>
            </h1>
            <p class="text-subtitle-1 mb-6">{{ $t('reservation_statements_description') }}</p>

            <div class="d-flex flex-wrap align-center mb-6">
                <div class="me-4 d-flex align-center">
                    <span class="me-2">{{ $t('period') }}:</span>
                    <span class="font-weight-bold">
                        <template v-if="monthYear">
                            {{ formatPeriod(monthYear) }}
                        </template>
                        <template v-else-if="dateKey">
                            {{ formatSingleDate(parseISO(dateKey)) }}
                        </template>
                        <template v-else-if="startDate && endDate">
                            {{ formatDateRange(startDate, endDate) }}
                        </template>
                        <template v-else>
                            {{ formatPeriod(selectedPeriod) }}
                        </template>
                    </span>
                </div>
                <v-spacer />
                <div class="d-flex">
                    <v-btn
                        color="primary"
                        variant="tonal"
                        class="me-2"
                        prepend-icon="mdi-file-pdf-box"
                        @click="downloadPDF"
                    >
                        {{ $t('download_pdf') }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-file-delimited-outline"
                        @click="downloadCSV"
                    >
                        {{ $t('download_csv') }}
                    </v-btn>
                </div>
            </div>

            <reservation-statement-table ref="statementTable" :reservations="reservations" currency="EUR" />

            <div v-if="isLoading" class="d-flex justify-center align-center pa-4">
                <v-progress-circular indeterminate color="primary" />
            </div>
        </v-card>
    </div>
</template>

<script setup lang="ts">
    import ReservationStatementTable from '../components/supplier/ReservationStatementTable.vue'
    import {ref, computed, onMounted, watch} from 'vue'
    import {generateReservationStatementPDF} from '../utils/pdf-generator'
    import {generateSimplePDF} from '../utils/simple-pdf-generator'
    import {authStore} from '~/composables/store-accessor'
    import {format, parseISO} from 'date-fns'

    const route = useRoute()
    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    // Helper function to normalize order ID for comparison with reservationNumber
    function normalizeOrderIdForComparison(orderId) {
        // Convert orderId to string and pad with leading zeros to match reservationNumber format
        // reservationNumber in transaction API response ALWAYS has length of 8
        return orderId.toString().padStart(8, '0')
    }

    // Helper function to check if a reservationNumber matches any of our order IDs
    function doesReservationNumberMatchOrderIds(reservationNumber, orderIds) {
        if (!reservationNumber) return false

        // reservationNumber is always 8 characters, so normalize all order IDs to 8 characters
        const normalizedIds = new Set(orderIds.map(id => normalizeOrderIdForComparison(id)))
        return normalizedIds.has(reservationNumber)
    }

    // Fetch PSP commission data for orders
    async function fetchPspCommissionForOrdersDetailed(orders, startDateStr, endDateStr) {
        try {
            const orderPspCommissions = new Map() // Map orderId -> pspCommission
            const orderIds = orders.map(order => order.orderId)

            if (orderIds.length === 0) {
                return orderPspCommissions
            }

            // We'll use the flexible matching function instead of pre-creating a map

            // Fetch transactions data for the orders in this period
            let offset = 0
            let hasMoreTransactions = true

            while (hasMoreTransactions) {
                const transactionParams = {
                    limit: 100,
                    offset: offset,
                    orderType: authStore.orderType || 'B2C',
                    // Add timestamp to prevent caching of transaction data
                    _t: Date.now(),
                }

                // Add date range filter if needed
                if (startDateStr && endDateStr) {
                    transactionParams.dateFrom = startDateStr
                    transactionParams.dateTo = endDateStr
                }

                try {
                    // Ensure fresh data by adding cache-busting headers
                    const transactionResponse = await $api.onlinePayments.get(transactionParams, null, {
                        headers: {
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            Pragma: 'no-cache',
                            Expires: '0',
                        },
                    })

                    if (
                        transactionResponse.onlinePaymentTransactions &&
                        transactionResponse.onlinePaymentTransactions.length > 0
                    ) {
                        // Map transactions to orders and store pspCommission
                        transactionResponse.onlinePaymentTransactions.forEach(transaction => {
                            if (doesReservationNumberMatchOrderIds(transaction.reservationNumber, orderIds)) {
                                // Use pspCommission field if available, otherwise default to 0
                                const pspCommission = transaction.pspCommission || 0

                                // Extract the actual order ID from the reservation number
                                const actualOrderId = parseInt(transaction.reservationNumber, 10)

                                // Sum up PSP commissions for the same order (in case of multiple transactions)
                                const existingCommission = orderPspCommissions.get(actualOrderId) || 0
                                orderPspCommissions.set(actualOrderId, existingCommission + pspCommission)
                            }
                        })

                        // Check if we need to fetch more transactions
                        if (transactionResponse.onlinePaymentTransactions.length < 100) {
                            hasMoreTransactions = false
                        } else {
                            offset += 100
                        }
                    } else {
                        hasMoreTransactions = false
                    }
                } catch (transactionError) {
                    hasMoreTransactions = false
                }
            }

            return orderPspCommissions
        } catch (error) {
            return new Map()
        }
    }

    // Breadcrumb related properties
    const isB2C = computed(() => authStore.isB2C)
    const userGuideLinkVideo = computed(() => {
        const meta = route.meta as any
        return meta?.userGuide?.video
    })
    const userGuideLinkConfluence = computed(() => {
        const meta = route.meta as any
        return meta?.userGuide?.link
    })

    // Get year from query params if available
    const year = computed(() => (route.query.year as string) || null)

    // Create breadcrumb items with link back to Supplier Invoices
    const breadcrumbItems = computed(() => {
        // Prepare query parameters for the breadcrumb link
        const query: Record<string, string> = {}

        // Add supplier ID if available
        if (supplierId.value) {
            query.supplierId = supplierId.value
        }

        // Add year if available to preserve it when navigating back
        if (year.value) {
            query.year = year.value
        }

        const items = [
            {
                title: t('home'),
                to: {name: 'home'},
                exact: true,
            },
            {
                title: t('reservation_statements'),
                to: {
                    name: 'reservationStatements',
                    query: Object.keys(query).length > 0 ? query : undefined,
                },
                exact: true,
            },
            {
                title: t('reservation_statements'),
                disabled: true,
            },
        ]

        return items
    })

    const statementTable = ref(null)
    const isLoading = ref(false)
    const reservations = ref([])
    const reservationNumber = computed(() => route.params.reservationNumber as string)

    // Check if we have a statement ID (format: statement-YYYY-MM)
    const isStatementId = computed(() => reservationNumber.value.startsWith('statement-'))

    // For backward compatibility, check if we have a range of order IDs
    const orderIds = computed(() => {
        // If it's a statement ID, we don't need to parse order IDs
        if (isStatementId.value) {
            return {start: null, end: null}
        }

        if (reservationNumber.value.includes('-')) {
            const [start, end] = reservationNumber.value.split('-').map(id => parseInt(id.replace(/^0+/, ''), 10))
            return {start, end}
        }
        return {start: parseInt(reservationNumber.value.replace(/^0+/, ''), 10), end: null}
    })

    // Check if we have date range in query params
    const startDate = computed(() => (route.query.startDate ? parseISO(route.query.startDate as string) : null))
    const endDate = computed(() => (route.query.endDate ? parseISO(route.query.endDate as string) : null))
    const monthYear = computed(() => (route.query.monthYear as string) || null)
    const dateKey = computed(() => (route.query.dateKey as string) || null)
    const supplierId = computed(() => (route.query.supplierId as string) || null)
    const supplierName = computed(() => (route.query.supplierName as string) || null)

    // Generate period options (YYYY-MM format)
    const currentDate = new Date()
    const availablePeriods = []
    for (let i = 0; i < 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1)
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        availablePeriods.push(`${year}-${month}`)
    }

    // Use monthYear from query params if available, otherwise use current month
    const selectedPeriod = ref(monthYear.value || availablePeriods[0])

    onMounted(async () => {
        await loadReservationData()
    })

    function getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel) {
        switch (sustainabilityLevel) {
            case 'ZERO':
                return 10
            case 'SUSTAINABLE':
                return 5
            case 'BASIC':
            default:
                return 15
        }
    }

    // Utility function to validate and compare reservation data
    function validateReservationData(reservations, context = '') {
        const validation = {
            context,
            totalReservations: reservations.length,
            totalOriginalAmount: 0,
            totalFinalAmount: 0,
            totalPaymentCharge: 0,
            totalCommissionAmount: 0,
            commissionBreakdown: {ZERO: 0, SUSTAINABLE: 0, BASIC: 0},
            issues: [],
        }

        reservations.forEach((reservation, index) => {
            // Validate required fields
            if (!reservation.originalAmount && reservation.originalAmount !== 0) {
                validation.issues.push(`Reservation ${index}: Missing originalAmount`)
            }
            if (!reservation.finalAmount && reservation.finalAmount !== 0) {
                validation.issues.push(`Reservation ${index}: Missing finalAmount`)
            }
            if (reservation.paymentCharge === undefined || reservation.paymentCharge === null) {
                validation.issues.push(`Reservation ${index}: Missing paymentCharge`)
            }
            if (!reservation.commissionAmount && reservation.commissionAmount !== 0) {
                validation.issues.push(`Reservation ${index}: Missing commissionAmount`)
            }

            // Check if finalAmount equals originalAmount - paymentCharge - commissionAmount
            const expectedFinalAmount =
                reservation.originalAmount - reservation.paymentCharge - reservation.commissionAmount
            if (Math.abs(reservation.finalAmount - expectedFinalAmount) > 0.01) {
                validation.issues.push(
                    `Reservation ${index}: finalAmount (${reservation.finalAmount}) != originalAmount - paymentCharge - commissionAmount (${expectedFinalAmount})`
                )
            }

            // Accumulate totals
            validation.totalOriginalAmount += reservation.originalAmount || 0
            validation.totalFinalAmount += reservation.finalAmount || 0
            validation.totalPaymentCharge += reservation.paymentCharge || 0
            validation.totalCommissionAmount += reservation.commissionAmount || 0

            // Track commission by sustainability level
            const level = reservation.sustainabilityLevel || 'BASIC'
            validation.commissionBreakdown[level] += reservation.commissionAmount || 0
        })

        // Validation logging removed for production
        return validation
    }

    async function loadReservationData() {
        isLoading.value = true
        try {
            // Extract period from selectedPeriod (YYYY-MM)
            const [year, month] = selectedPeriod.value.split('-')
            const periodDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1)

            // Determine if we need to fetch orders based on date range or order IDs
            let ordersToProcess = []

            // If we have a statement ID or month-year query param, fetch orders by date range
            if (isStatementId.value || monthYear.value) {
                // Use the month-year from the query params or extract it from the statement ID
                const monthYearToUse =
                    monthYear.value || (isStatementId.value ? reservationNumber.value.replace('statement-', '') : null)

                if (monthYearToUse) {
                    // Parse the month-year to get start and end dates
                    const [year, month] = monthYearToUse.split('-')
                    const startDateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)
                    const endDateObj = new Date(parseInt(year, 10), parseInt(month, 10), 0) // Last day of the month

                    // Format dates for API request
                    const startDateStr = format(startDateObj, 'yyyy-MM-dd')
                    const endDateStr = format(endDateObj, 'yyyy-MM-dd')

                    // Fetch orders for the month using service end date (checkout) with pagination
                    // Use exact date range to ensure orders are only in one month

                    // Initialize variables for pagination
                    let allFetchedOrders = []
                    let offset = 0
                    let hasMoreOrders = true

                    // Fetch orders in batches until we have all of them
                    while (hasMoreOrders) {
                        try {
                            // Prepare API request parameters
                            const requestParams: Record<string, any> = {
                                serviceEndDateFrom: startDateStr,
                                serviceEndDateTo: endDateStr,
                                orderType: authStore.orderType || 'B2C',
                                responseType: 'SHORT',
                                limit: 100, // Maximum allowed limit
                                offset: offset,
                            }

                            // Add supplier filter if a specific supplier is selected
                            if (supplierId.value) {
                                requestParams.supplierId = supplierId.value
                            }

                            const response = await $api.orders.get(requestParams)

                            // If we got orders, add them to our collection
                            if (response.orders && response.orders.length > 0) {
                                allFetchedOrders = [...allFetchedOrders, ...response.orders]

                                // Check if we need to fetch more orders
                                const totalOrdersCount = response.ordersCount || 0

                                // If we've fetched all orders, stop the loop
                                if (allFetchedOrders.length >= totalOrdersCount) {
                                    hasMoreOrders = false
                                } else {
                                    // Otherwise, update the offset for the next batch
                                    offset += response.orders.length
                                }
                            } else {
                                // No orders returned, stop the loop
                                hasMoreOrders = false
                            }
                        } catch (error) {
                            hasMoreOrders = false
                        }
                    }

                    if (allFetchedOrders.length > 0) {
                        ordersToProcess = allFetchedOrders
                    }
                }
            } else if (orderIds.value.end) {
                // We have a range of order IDs (backward compatibility)

                // Calculate total number of orders in the range
                const totalOrdersInRange = orderIds.value.end - orderIds.value.start + 1

                // Fetch orders in batches of 100
                const batches = Math.ceil(totalOrdersInRange / 100)

                for (let batch = 0; batch < batches; batch++) {
                    const batchStart = orderIds.value.start + batch * 100
                    const batchEnd = Math.min(orderIds.value.end, batchStart + 99)
                    const batchSize = batchEnd - batchStart + 1

                    // Use the orders API to fetch this batch of orders
                    try {
                        const response = await $api.orders.get({
                            orderIds: Array.from({length: batchSize}, (_, i) => batchStart + i).join(','),
                            orderType: authStore.orderType || 'B2C',
                            responseType: 'SHORT',
                            limit: 100, // Maximum allowed limit
                        })

                        if (response?.orders?.length > 0) {
                            ordersToProcess = [...ordersToProcess, ...response.orders]
                        }
                    } catch (error) {
                        // Error handled - continue processing remaining batches
                    }
                }
            } else if (orderIds.value.start) {
                // Single order ID (backward compatibility)

                // Get order details using the orders API
                const response = await $api.orders.get({
                    orderId: orderIds.value.start,
                    orderType: authStore.orderType || 'B2C',
                    responseType: 'SHORT',
                })

                if (response?.orders?.length > 0) {
                    ordersToProcess = response.orders
                }
            }

            if (ordersToProcess.length === 0) {
                $toast.error(t('error_message.reservation_not_found'))
                return
            }

            // Process all orders and collect reservations
            const allReservations = []

            // Process each order
            for (const order of ordersToProcess) {
                // Process each service in the order
                if (order.services && order.services.length > 0) {
                    for (const service of order.services) {
                        // Only include accommodation services
                        if (service.serviceType === 'ACCOMMODATION') {
                            // Extract check-in and check-out dates from service
                            const checkIn = service.startDateTime ? new Date(service.startDateTime) : null
                            const checkOut = service.endDateTime ? new Date(service.endDateTime) : null

                            if (!checkIn || !checkOut) return

                            // Check if the service is in the selected period or date range
                            let includeService = false

                            if (monthYear.value) {
                                // Use month-year from query params - strict filtering by checkout date
                                const [yearStr, monthStr] = monthYear.value.split('-')
                                const year = parseInt(yearStr, 10)
                                const month = parseInt(monthStr, 10) - 1 // JavaScript months are 0-indexed

                                // Strict check by year and month to ensure orders only appear in one month
                                includeService = checkOut.getFullYear() === year && checkOut.getMonth() === month
                            } else if (dateKey.value) {
                                // Use specific date from query params - filter by checkout date
                                includeService = format(checkOut, 'yyyy-MM-dd') === dateKey.value
                            } else if (startDate.value && endDate.value) {
                                // Use date range from query params - filter by checkout date
                                includeService = checkOut >= startDate.value && checkOut <= endDate.value
                            } else {
                                // Use selected period - filter by checkout date
                                includeService =
                                    checkOut.getFullYear() === periodDate.getFullYear() &&
                                    checkOut.getMonth() === periodDate.getMonth()
                            }

                            if (includeService) {
                                // Calculate nights
                                const nights = Math.round(
                                    (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
                                )

                                // Get room type information
                                let roomTypeName = 'Standard'
                                let sustainabilityLevel = 'BASIC'
                                let commissionPercentage =
                                    getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel) // Default commission based on BASIC level

                                if (service.serviceDetailsContainer?.accommodation?.rooms?.length > 0) {
                                    const room = service.serviceDetailsContainer.accommodation.rooms[0]
                                    roomTypeName = room.roomTypeName || 'Standard'

                                    // Use sustainability level directly from API response (SHORT response includes this field)
                                    sustainabilityLevel = room.sustainabilityLevel || 'BASIC'

                                    // Determine commission percentage based on sustainability level
                                    commissionPercentage =
                                        getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel)
                                }

                                // Get guest name from travelers
                                let guestName = 'Guest'
                                if (service.travelers && service.travelers.length > 0) {
                                    const mainTraveler =
                                        service.travelers.find((t: any) => t.isTourLead) || service.travelers[0]
                                    if (mainTraveler.name && mainTraveler.name.length > 0) {
                                        const name = mainTraveler.name[0]
                                        guestName = `${name.firstName || ''} ${name.lastName || ''}`.trim()
                                    }
                                }

                                // Get price information from salesTerms
                                let originalAmount = 0

                                if (service.salesTerms && service.salesTerms.length > 0) {
                                    // Find the CLIENT price terms
                                    const clientTerms = service.salesTerms.find((term: any) => term.type === 'CLIENT')
                                    if (clientTerms && clientTerms.price) {
                                        originalAmount = clientTerms.price.amount || 0
                                    }
                                }

                                // Calculate commission amount
                                const commissionAmount = (originalAmount * commissionPercentage) / 100

                                // Payment charge will be fetched from transactions API later
                                const paymentCharge = 0

                                // Determine result status
                                let result = 'Stayed'
                                if (service.status) {
                                    if (service.status.toLowerCase() === 'canceled') {
                                        result = 'Cancelled'
                                    } else {
                                        result = service.status
                                    }
                                }

                                const reservation = {
                                    bookNumber: `${order.orderId}`,
                                    guestName: guestName,
                                    checkIn: checkIn,
                                    checkOut: checkOut,
                                    nights: nights,
                                    commissionPercentage: commissionPercentage,
                                    result: result,
                                    originalAmount: originalAmount,
                                    finalAmount: originalAmount - paymentCharge - commissionAmount,
                                    paymentCharge: paymentCharge,
                                    commissionAmount: commissionAmount,
                                    roomTypeName: roomTypeName,
                                    sustainabilityLevel: sustainabilityLevel, // Add sustainability level for reference
                                    orderId: order.orderId, // Add orderId for PSP commission lookup
                                }

                                allReservations.push(reservation)
                            }
                        }
                    }
                }
            }

            // Fetch PSP commission data for all reservations
            if (allReservations.length > 0) {
                // Get unique order IDs
                const orderIds = [...new Set(allReservations.map(r => r.orderId))]
                const orders = orderIds.map(orderId => ({orderId}))

                // Determine date range for PSP commission fetching
                let startDateStr = null
                let endDateStr = null

                if (startDate.value && endDate.value) {
                    startDateStr = format(startDate.value, 'yyyy-MM-dd')
                    endDateStr = format(endDate.value, 'yyyy-MM-dd')
                } else if (monthYear.value) {
                    const [year, month] = monthYear.value.split('-')
                    const monthStart = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)
                    const monthEnd = new Date(parseInt(year, 10), parseInt(month, 10), 0)
                    startDateStr = format(monthStart, 'yyyy-MM-dd')
                    endDateStr = format(monthEnd, 'yyyy-MM-dd')
                }

                // Fetch PSP commission data
                const orderPspCommissions = await fetchPspCommissionForOrdersDetailed(orders, startDateStr, endDateStr)

                // Update payment charges in reservations
                // For multi-room orders (same orderId), only the first room should get the PSP commission
                // Track which orderIds we've already assigned PSP commission to
                const orderIdsWithAssignedFee = new Set()

                allReservations.forEach(reservation => {
                    const pspCommission = orderPspCommissions.get(reservation.orderId) || 0

                    // Only assign PSP commission to the first room in a multi-room order
                    if (!orderIdsWithAssignedFee.has(reservation.orderId)) {
                        reservation.paymentCharge = pspCommission
                        orderIdsWithAssignedFee.add(reservation.orderId)
                    } else {
                        // For subsequent rooms in the same order, set payment charge to 0
                        reservation.paymentCharge = 0
                    }

                    // Recalculate final amount to include the updated payment charge
                    reservation.finalAmount =
                        reservation.originalAmount - reservation.paymentCharge - reservation.commissionAmount
                })
            }

            // If no reservations were found, show a message
            if (allReservations.length === 0) {
                $toast.warning(t('error_message.no_reservation_data'))
            }

            // Debug logging for individual statement totals
            const totalOriginalAmount = allReservations.reduce((sum, item) => sum + item.originalAmount, 0)
            const totalFinalAmount = allReservations.reduce((sum, item) => sum + item.finalAmount, 0)
            const totalPaymentCharge = allReservations.reduce((sum, item) => sum + item.paymentCharge, 0)
            const totalCommissionAmount = allReservations.reduce((sum, item) => sum + item.commissionAmount, 0)

            // Validate reservation data
            validateReservationData(allReservations, `Individual Statement - ${selectedPeriod.value}`)

            // Sort reservations by order ID (ascending)
            allReservations.sort((a, b) => {
                const orderIdA = parseInt(a.bookNumber)
                const orderIdB = parseInt(b.bookNumber)
                return orderIdA - orderIdB
            })

            // Update the reservations
            reservations.value = allReservations
        } catch (error) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    // Format period for display (YYYY-MM to Month YYYY)
    function formatPeriod(period: string): string {
        if (!period) return ''

        const [year, month] = period.split('-')
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1)

        return date.toLocaleString('en-US', {month: 'long', year: 'numeric'})
    }

    // Format date range for display
    function formatDateRange(start: Date, end: Date): string {
        if (!start || !end) return ''

        return `${format(start, 'MMMM d, yyyy')} - ${format(end, 'MMMM d, yyyy')}`
    }

    // Format single date for display
    function formatSingleDate(date: Date): string {
        if (!date) return ''

        return format(date, 'MMMM d, yyyy')
    }

    // Watch for period changes to reload data
    watch(selectedPeriod, async () => {
        await loadReservationData()
    })

    function downloadPDF() {
        try {
            if (!reservations.value || reservations.value.length === 0) {
                $toast.warning(t('error_message.no_data_to_download'))
                return
            }

            // Determine the period to display
            let periodText = ''
            let filename = ''

            if (monthYear.value) {
                // Use month-year from query params
                periodText = formatPeriod(monthYear.value)
                filename = `monthly-statement-${monthYear.value}.pdf`
            } else if (dateKey.value) {
                // Use specific date from query params
                const date = parseISO(dateKey.value)
                periodText = formatSingleDate(date)
                filename = `daily-statement-${dateKey.value}.pdf`
            } else if (startDate.value && endDate.value) {
                // Use date range from query params
                periodText = `${format(startDate.value, 'MMMM d, yyyy')} - ${format(endDate.value, 'MMMM d, yyyy')}`
                filename = `reservation-statement-${format(startDate.value, 'yyyy-MM-dd')}-to-${format(endDate.value, 'yyyy-MM-dd')}.pdf`
            } else {
                // Use selected period
                periodText = formatPeriod(selectedPeriod.value)
                filename = `reservation-statement-${reservationNumber.value}.pdf`
            }

            // Create title with supplier name if applicable
            const pdfTitle = supplierName.value
                ? `${t('reservation_statements')} - ${supplierName.value}`
                : t('reservation_statements')

            // Add supplier name to filename if applicable
            if (supplierName.value) {
                const supplierSlug = supplierName.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                filename = filename.replace('.pdf', `-${supplierSlug}.pdf`)
            }

            // Try to generate PDF with our simple generator
            try {
                generateSimplePDF({
                    reservations: reservations.value,
                    currency: 'EUR',
                    title: pdfTitle,
                    period: periodText,
                    filename: filename,
                })
                $toast.success(t('success_message.pdf_generated'))
            } catch (simplePdfError) {
                // Fall back to the original generator
                try {
                    generateReservationStatementPDF({
                        reservations: reservations.value,
                        currency: 'EUR',
                        title: pdfTitle,
                        period: periodText,
                        filename: filename,
                    })
                    $toast.success(t('success_message.pdf_generated'))
                } catch (fallbackError: any) {
                    $toast.error(`${t('error_message.download_error')}: ${fallbackError.message || 'Unknown error'}`)
                }
            }
        } catch (error: any) {
            $toast.error(`${t('error_message.download_error')}: ${error.message || 'Unknown error'}`)
        }
    }

    function downloadCSV() {
        try {
            if (!reservations.value || reservations.value.length === 0) {
                $toast.warning(t('error_message.no_data_to_download'))
                return
            }

            // Determine the filename
            let filename = ''
            if (monthYear.value) {
                filename = `monthly-statement-${monthYear.value}.csv`
            } else if (dateKey.value) {
                filename = `daily-statement-${dateKey.value}.csv`
            } else if (startDate.value && endDate.value) {
                filename = `reservation-statement-${format(startDate.value, 'yyyy-MM-dd')}-to-${format(endDate.value, 'yyyy-MM-dd')}.csv`
            } else {
                filename = `reservation-statement-${reservationNumber.value}.csv`
            }

            // Add supplier name to filename if applicable
            if (supplierName.value) {
                const supplierSlug = supplierName.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                filename = filename.replace('.csv', `-${supplierSlug}.csv`)
            }

            // Define CSV headers
            const headers = [
                'Book Number',
                'Guest Name',
                'Room Type',
                'Check In',
                'Check Out',
                'Nights',
                'Commission %',
                'Result',
                'Original Amount',
                'Final Amount',
                'Payment Charge',
                'Commission Amount',
            ]

            // Helper function to format sustainability level
            const formatSustainabilityLevel = level => {
                if (!level) return 'Basic'
                switch (level.toUpperCase()) {
                    case 'ZERO':
                        return 'Zero'
                    case 'SUSTAINABLE':
                        return 'Sustainable'
                    case 'BASIC':
                    default:
                        return 'Basic'
                }
            }

            // Format data for CSV
            const csvData = reservations.value.map(item => [
                item.bookNumber,
                item.guestName,
                formatSustainabilityLevel(item.sustainabilityLevel),
                format(item.checkIn, 'yyyy-MM-dd'),
                format(item.checkOut, 'yyyy-MM-dd'),
                item.nights,
                item.commissionPercentage,
                item.result,
                item.originalAmount.toFixed(2),
                item.finalAmount.toFixed(2),
                item.paymentCharge.toFixed(2),
                item.commissionAmount.toFixed(2),
            ])

            // Add totals row
            const totalOriginalAmount = reservations.value.reduce((sum, item) => sum + item.originalAmount, 0)
            const totalFinalAmount = reservations.value.reduce((sum, item) => sum + item.finalAmount, 0)
            const totalPaymentCharge = reservations.value.reduce((sum, item) => sum + item.paymentCharge, 0)
            const totalCommissionAmount = reservations.value.reduce((sum, item) => sum + item.commissionAmount, 0)

            csvData.push([
                'Total',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                totalOriginalAmount.toFixed(2),
                totalFinalAmount.toFixed(2),
                totalPaymentCharge.toFixed(2),
                totalCommissionAmount.toFixed(2),
            ])

            // Convert to CSV string
            const csvContent = [
                headers.join(','),
                ...csvData.map(row =>
                    row
                        .map(cell => {
                            // Escape quotes and wrap text fields in quotes
                            if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
                                return `"${cell.replace(/"/g, '""')}"`
                            }
                            return cell
                        })
                        .join(',')
                ),
            ].join('\n')

            // Create a Blob and download
            const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
            const url = URL.createObjectURL(blob)

            // Create a link and trigger download
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            $toast.success(t('success_message.csv_generated'))
        } catch (error: any) {
            $toast.error(`${t('error_message.download_error')}: ${error.message || 'Unknown error'}`)
        }
    }
</script>

<style scoped>
    .reservation-table {
        width: 100%;
        border-collapse: collapse;
    }

    .reservation-table th,
    .reservation-table td {
        padding: 12px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }

    .reservation-table th {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        white-space: nowrap;
    }

    .reservation-table td {
        color: rgba(0, 0, 0, 0.87);
    }

    .reservation-row:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }

    .total-row td {
        padding-top: 16px;
        padding-bottom: 16px;
        font-weight: 500;
    }

    /* Column widths */
    .reservation-table th:nth-child(1),
    .reservation-table td:nth-child(1) {
        min-width: 120px;
    }

    .reservation-table th:nth-child(2),
    .reservation-table td:nth-child(2) {
        min-width: 150px;
    }

    .reservation-table th:nth-child(3),
    .reservation-table td:nth-child(3) {
        min-width: 150px;
    }

    .reservation-table th:nth-child(4),
    .reservation-table td:nth-child(4) {
        min-width: 80px;
    }

    .reservation-table th:nth-child(5),
    .reservation-table td:nth-child(5) {
        min-width: 80px;
    }

    .reservation-table th:nth-child(6),
    .reservation-table td:nth-child(6) {
        min-width: 100px;
    }

    .reservation-table th:nth-child(7),
    .reservation-table td:nth-child(7),
    .reservation-table th:nth-child(8),
    .reservation-table td:nth-child(8),
    .reservation-table th:nth-child(9),
    .reservation-table td:nth-child(9),
    .reservation-table th:nth-child(10),
    .reservation-table td:nth-child(10) {
        min-width: 120px;
    }

    @media print {
        .v-btn {
            display: none !important;
        }

        .reservation-table {
            border: none;
        }

        .reservation-table th,
        .reservation-table td {
            padding: 8px;
        }
    }
</style>
